import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ADMIN_EMAIL } from '@/lib/supabase';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn, isAdmin, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && isAdmin) {
      navigate('/admin/dashboard');
    }
  }, [isAdmin, authLoading, navigate]);

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setError('Vui lòng nhập đầy đủ email và mật khẩu.');
      return;
    }
    setLoading(true);
    setError('');
    const { error: err } = await signIn(email, password);
    setLoading(false);
    if (err) {
      setError('Email hoặc mật khẩu không đúng. Vui lòng thử lại.');
      return;
    }
    // isAdmin check will trigger useEffect redirect
    if (email.trim().toLowerCase() !== ADMIN_EMAIL.toLowerCase()) {
      setError('Tài khoản này không có quyền truy cập trang quản trị.');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3d1a05 0%, #8a3a10 50%, #c4621e 100%)' }}>
        <div className="text-white text-lg font-semibold">Đang kiểm tra...</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #3d1a05 0%, #8a3a10 50%, #c4621e 100%)' }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="relative w-24 h-24 mx-auto mb-5">
            <div className="w-24 h-24 flex items-center justify-center rounded-3xl backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <i className="ri-shield-star-fill text-5xl text-white"></i>
            </div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 flex items-center justify-center bg-yellow-400 rounded-xl">
              <i className="ri-edit-2-fill text-white text-sm"></i>
            </div>
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-1">Nhà Phát Hành</h1>
          <p className="text-white/70 text-sm font-medium">Túi Trò Chơi Dân Gian Bahnar</p>
        </div>

        {/* Permissions info */}
        <div className="mb-5 rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}>
          <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-3">Quyền hạn nhà phát hành</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { icon: 'ri-video-add-fill', text: 'Đăng tải video' },
              { icon: 'ri-article-fill', text: 'Quản lý bài đăng' },
              { icon: 'ri-book-2-fill', text: 'Quản lý KHBD' },
              { icon: 'ri-gamepad-fill', text: 'Quản lý trò chơi' },
              { icon: 'ri-home-4-fill', text: 'Chỉnh sửa trang chủ' },
              { icon: 'ri-eye-fill', text: 'Ẩn/hiện nội dung' },
            ].map(item => (
              <div key={item.text} className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-yellow-300 text-sm`}></i>
                </div>
                <span className="text-white/80 text-xs">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-2xl">
              <i className="ri-login-circle-fill text-orange-500 text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Đăng nhập hệ thống</h2>
              <p className="text-xs text-gray-400">Chỉ dành cho nhà phát hành được ủy quyền</p>
            </div>
          </div>

          <div className="space-y-4 mb-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email nhà phát hành</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-mail-fill text-sm"></i>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  placeholder={ADMIN_EMAIL}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mật khẩu</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-lock-fill text-sm"></i>
                </div>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  placeholder="Nhập mật khẩu..."
                  className="w-full pl-10 pr-11 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                />
                <button
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-orange-500 cursor-pointer"
                >
                  <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl mb-4">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-fill text-red-500"></i>
              </div>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <i className="ri-shield-keyhole-fill"></i>
            {loading ? 'Đang xác thực...' : 'Đăng nhập với tư cách Nhà Phát Hành'}
          </button>
        </div>

        <p className="text-center text-white/40 text-xs mt-5">
          Truy cập trái phép sẽ bị ghi lại và xử lý theo quy định
        </p>
      </div>
    </div>
  );
}
